import { defineComponent as _defineComponent } from 'vue'
import { Component, computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  NavBar,
  Tabs,
  Tab,
  InitialAvatarIcon,
  TabList
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { FixedIncomeProducts } from './components/FixedIncomeProducts';
import { GovernmentBondProducts } from './components/GovernmentBondProducts';
import { FundsProducts } from './components/FundsProducts';
import { CustomOperationsProducts } from './components/CustomOperationsProducts';
import { InitialPublicOfferingsProducts } from './components/InitialPublicOfferingsProducts';
import { ConfirmExitModal } from './components/ConfirmExitModal';
import { IProductGetLastState } from '@/modules/catalog/services/products-acquisition';
import ProductSideSheet from './components/ProductSideSheet/ProductSideSheet.vue';
import { BlockedInvestmentModal } from '@/modules/catalog/components';
import { IInvestmentProfile } from '@/types/models';
import { getCustomerInvestmentProfile } from '@/modules/common/services/http/account';
import { CustomerModule } from '@/modules/common/store/CustomerModule';
import { EProductTypes } from '@/modules/catalog/services/products/products-types';
import { useCatalogStore } from '@/modules/catalog/store';

enum EProductsTabsIndexes {
  FixedIncome = 0,
  GovernmentBond = 1,
  Funds = 2,
  CustomOperations = 3,
  InitialPublicOfferings = 4
}

interface Props {
  isLoadingButtonOnDetail?: boolean;
  products?: IProductGetLastState[];
  closeModal?: boolean;
  isBuilder: boolean;
  cameFrom: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'Products',
  props: {
    isLoadingButtonOnDetail: { type: Boolean, default: false },
    products: { default: undefined },
    closeModal: { type: Boolean },
    isBuilder: { type: Boolean },
    cameFrom: null
  },
  emits: ['add-product', 'remove-product'],
  setup(__props: any, { emit }) {

const props = __props as { isLoadingButtonOnDetail: boolean, products: IProductGetLastState[], closeModal?: boolean, isBuilder: boolean, cameFrom: string };





const stepComponent: Record<EProductTypes, Component> = {
  [EProductTypes.FIXED_INCOME]: FixedIncomeProducts,
  [EProductTypes.FUNDS]: FundsProducts,
  [EProductTypes.GOVERNMENT_BOND]: GovernmentBondProducts,
  [EProductTypes.STRUCTURED_OPERATION]: CustomOperationsProducts,
  [EProductTypes.INITIAL_PUBLIC_OFFERING]: InitialPublicOfferingsProducts
};

const colorMapper: Record<EProductTypes, NebraskaColors> = {
  [EProductTypes.FIXED_INCOME]: NebraskaColors.asset04,
  [EProductTypes.GOVERNMENT_BOND]: NebraskaColors.asset06,
  [EProductTypes.FUNDS]: NebraskaColors.asset12,
  [EProductTypes.STRUCTURED_OPERATION]: NebraskaColors.asset02,
  [EProductTypes.INITIAL_PUBLIC_OFFERING]: NebraskaColors.asset08
};

const router = useRouter();
const route = useRoute();

const currentTabIndex = ref(EProductsTabsIndexes.FixedIncome);
const selectedProductId = ref('');
const categorySelectedProduct = ref(route.params.type as EProductTypes);
const isConfirmModalOpen = ref(false);
const isBlockedInvestment = ref(false);
const blockInvestmentType = ref('');
const investmentProfile = ref<IInvestmentProfile>();

async function fetchPersonalProfile() {
  try {
    const data = await getCustomerInvestmentProfile(
      CustomerModule.getCustomerId
    );
    investmentProfile.value = data.investmentProfile;
  } catch (error) {
    console.error('Erro ao buscar perfil de investimento:', error);
  }
}

const tabs = computed(() => {
  const baseTabs = [
    {
      label: 'Renda fixa',
      icon: 'BA0011',
      key: EProductTypes.FIXED_INCOME
    },
    {
      label: 'Tesouro direto',
      icon: 'CA0100',
      key: EProductTypes.GOVERNMENT_BOND
    },
    {
      label: 'Fundos',
      icon: 'EF0110',
      key: EProductTypes.FUNDS
    },
    {
      label: 'Op. personalizadas',
      icon: 'BB0011',
      key: EProductTypes.STRUCTURED_OPERATION
    }
  ];

  if (!props.isBuilder) {
    baseTabs.push({
      label: 'Ofertas públicas',
      icon: 'DA0080',
      key: EProductTypes.INITIAL_PUBLIC_OFFERING
    });
  }

  if (props.isBuilder) {
    const itemsToRemove = [
      EProductTypes.GOVERNMENT_BOND,
      EProductTypes.STRUCTURED_OPERATION
    ];

    return baseTabs.filter(item => !itemsToRemove.includes(item.key));
  }

  return baseTabs;
});

const step = computed(() => {
  return {
    component: stepComponent[currentTab.value.key]
  };
});

const canAddProduct = computed(() => {
  if (props.isBuilder) {
    return Boolean(
      !props.products.find(
        product =>
          product.productId === selectedProductId.value ||
          product.id === selectedProductId.value
      )
    );
  }

  return true;
});

const currentTab = computed(() => {
  return tabs.value[currentTabIndex.value];
});

const backgroundStyle = computed(() => {
  return { backgroundColor: colorMapper[currentTab.value.key] };
});

function onChangeTab(tab: EProductsTabsIndexes) {
  currentTabIndex.value = tab;

  const routerName = props.isBuilder
    ? `catalog/builder/${
        route.params.portfolioId ? route.params.portfolioId + '/' : ''
      }${currentTab.value.key}`
    : `products-view/catalog/${currentTab.value.key}`;

  window.history.pushState({}, '', `/app/#/${routerName}`);

  categorySelectedProduct.value = currentTab.value.key;
}

function handleExitCatalog() {
  if (props.isBuilder) {
    isConfirmModalOpen.value = true;
    return;
  }
  goBackCatalog();
}

function goBackCatalog() {
  if (useCatalogStore().fromUserFlow) {
    router.push({
      name: useCatalogStore().fromUserFlow,
      query: { cameFrom: useCatalogStore().fromUserCameFrom }
    });
  } else {
    const cameFrom = props.cameFrom || 'home';
    router.push({ name: cameFrom });
  }
}

function closeProductDetails() {
  selectedProductId.value = '';

  const url = props.isBuilder
    ? `catalog/builder/${
        route.params.portfolioId ? route.params.portfolioId + '/' : ''
      }`
    : 'products-view/catalog';

  window.history.pushState({}, '', `/app/#/${url}/${currentTab.value.key}`);
}

watch(
  () => props.closeModal,
  closeModal => {
    if (closeModal) {
      selectedProductId.value = '';
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await fetchPersonalProfile();
});

onBeforeMount(async () => {
  const tabIndexType: { [key: string]: number } = {};
  tabs.value.forEach((tab, index) => {
    tabIndexType[tab.key] = index;
  });

  const type = route.params.type || 'fixed-income';
  const key = type;

  currentTabIndex.value = tabIndexType[key];

  if (route.params?.productId) {
    selectedProductId.value = route.params.productId;
  }
});

const productSideSheetColor = computed(() => {
  return colorMapper[currentTab.value.key];
});

return { __sfc: true,EProductsTabsIndexes, props, emit, stepComponent, colorMapper, router, route, currentTabIndex, selectedProductId, categorySelectedProduct, isConfirmModalOpen, isBlockedInvestment, blockInvestmentType, investmentProfile, fetchPersonalProfile, tabs, step, canAddProduct, currentTab, backgroundStyle, onChangeTab, handleExitCatalog, goBackCatalog, closeProductDetails, productSideSheetColor, NavBar, Tabs, Tab, InitialAvatarIcon, TabList, BackButton, ConfirmExitModal, ProductSideSheet, BlockedInvestmentModal }
}

})